import React, { useCallback } from 'react';

import { Swap } from '@lyearn/icons';
import { DeprecatedIconButton, List, Popover, Typography } from '@lyearn/ui';

import MenuItems from './components/MenuItems';
import { swapStyle } from './style';
import { ListItemProps, SortMenuProps } from './types';
import useSort from './useSort';

const defaultSortButton = (disabled: boolean) => (
  <DeprecatedIconButton
    appearance="secondary"
    className="h-44 w-44 flex-shrink-0 bg-bg-primary"
    disabled={disabled}
    variant="outlined">
    <Swap className={swapStyle} />
  </DeprecatedIconButton>
);

const SortMenuDesktop = <T extends ListItemProps>(props: SortMenuProps<T>) => {
  const {
    overrides,
    children,
    clearable,
    disabled = false,
    label,
    showBottomSheet = false,
    actions,
    onSort,
    defaultSortBy,
  } = props;
  const { sortBy, setSortBy, sortActions } = useSort(actions, defaultSortBy);
  const onSortMenuClick = useCallback(
    (sort: string, item: T) => {
      const nextValue = clearable && sort === sortBy ? '' : sort;
      setSortBy(nextValue);
      onSort(nextValue, item);
    },
    [clearable, onSort, setSortBy, sortBy],
  );

  const className = showBottomSheet
    ? 'pb-20 pt-12 px-24 mb-8 text-text-primary border-b border-solid border-divider'
    : 'text-text-secondary text-start ms-10 mb-8 mt-4';

  return (
    <Popover>
      <Popover.Button classes={overrides?.PopoverButton?.classes} disabled={disabled}>
        {children || defaultSortButton(disabled)}
      </Popover.Button>
      <Popover.Content
        minWidth={17.6}
        position="bottom-start"
        renderAs={showBottomSheet ? 'bottom-sheet' : 'popover'}
        {...overrides?.PopoverContent}>
        <List className="py-12 px-8 xs:px-0" {...overrides?.BaseMenuList}>
          {label ? (
            <Typography
              as="p"
              className={className}
              variant={showBottomSheet ? 'heading-s' : 'label-caps'}>
              {label}
            </Typography>
          ) : null}
          <MenuItems
            actions={sortActions}
            overrides={overrides?.MenuItem}
            showBottomSheet={showBottomSheet}
            onMenuClick={onSortMenuClick}
          />
        </List>
      </Popover.Content>
    </Popover>
  );
};

export default React.memo(SortMenuDesktop) as typeof SortMenuDesktop;
